import React, { useEffect, useId, useRef, useState } from "react";
import ModalA11yWrapper from "../../ui/ModalA11yWrapper";
import Button from "../../ui/Button";
import Input from "../../ui/Input";

export default function ConfirmDialog({
  open = false,
  title,
  message,
  onConfirm,
  onCancel,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  confirmVariant = "destructive",
  cancelVariant = "secondary",
  typedConfirmationText,
  typedConfirmationLabel = "Type to confirm",
  typedConfirmationPlaceholder,
  confirmDisabled = false,
  backdropStyle,
  containerStyle,
}) {
  const cancelRef = useRef(null);
  const titleId = useId();
  const messageId = useId();
  const [typedValue, setTypedValue] = useState("");
  const requiresTyped = Boolean(typedConfirmationText);

  useEffect(() => {
    if (open) {
      setTypedValue("");
    }
  }, [open]);

  const handleCancel = () => {
    onCancel?.();
  };

  const handleConfirm = () => {
    onConfirm?.(typedValue);
  };

  const normalizedTyped = typedValue.trim().toLowerCase();
  const normalizedRequired = requiresTyped ? String(typedConfirmationText).trim().toLowerCase() : "";
  const typedMatches = !requiresTyped || normalizedTyped === normalizedRequired;
  const confirmIsDisabled = confirmDisabled || (requiresTyped && !typedMatches);

  return (
    <ModalA11yWrapper
      open={open}
      onClose={handleCancel}
      labelledBy={titleId}
      describedBy={messageId}
      initialFocusRef={cancelRef}
      role="alertdialog" // alertdialog ensures AT announces importance immediately.
      backdropStyle={backdropStyle}
      containerStyle={containerStyle}
    >
      <div className="confirm-dialog" style={{ padding: "16px", minWidth: "280px", borderRadius: "4px" }}>
        <h3 id={titleId}>{title || "Confirm"}</h3>
        {typeof message === "string" || message === undefined || message === null ? (
          <p id={messageId}>{message || "Are you sure?"}</p>
        ) : (
          <div id={messageId}>{message}</div>
        )}
        {requiresTyped && (
          <div style={{ marginTop: "12px" }}>
            <p style={{ margin: 0, fontSize: "13px" }}>
              Type <strong>{typedConfirmationText}</strong> to confirm.
            </p>
            <div style={{ marginTop: "8px" }}>
              <Input
                label={typedConfirmationLabel}
                value={typedValue}
                onChange={(event) => setTypedValue(event.target.value)}
                placeholder={typedConfirmationPlaceholder || String(typedConfirmationText)}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="none"
                spellCheck={false}
              />
            </div>
          </div>
        )}
        <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
          <Button type="button" variant={cancelVariant} onClick={handleCancel} ref={cancelRef}>
            {cancelLabel}
          </Button>
          <Button type="button" variant={confirmVariant} onClick={handleConfirm} disabled={confirmIsDisabled}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </ModalA11yWrapper>
  );
}
